import { useState } from 'react';
import Sheet from '../Sheet';
import ButtonSpinner from '../ButtonSpinner';
import { BTN_SECONDARY_ON_PAGE } from '../buttonStyles';
import { busyStyle } from '../../utils/busyStyle';
import { CARD, ROW_TEXT, ROW_VALUE, SECTION_FOOTNOTE, SECTION_HEADER } from './surfaces';
import { adjustLiveLessonTime } from '../../services/liveV2';
import { liveLessonsErrorMessage } from '../../services/liveLessons';
import { useToast } from '../../hooks/useToast';
import { whenLine } from '../../utils/liveCalendar';
import { firstNameOf } from '../../utils/liveLessonText';
import type { LiveCallBooking } from '../../types/liveCall';

/** Adjust Time's steps (docs/live-lessons-data.md, adjustLiveLessonTime). */
const STEPS = [5, 10, 15, 20, 30];

/**
 * Small moves (redesign spec, batches 4 and 5), always allowed, even five
 * minutes before. A strip of Earlier and Later by 5, 10, 15, 20 or 30; tap
 * one and it sends (adjustLiveLessonTime). The other side confirms with one
 * tap on the card. A bigger move is Reschedule. The sheet is titled
 * with the label that opened it ("Suggest a Time" in the lesson's menu,
 * "Move 4:00 PM Lesson" from the thread's chip), so the two never differ.
 */
export function AdjustTimeSheet({
  title, booking, isCoach, tz, onPickTime, onClose,
}: {
  /** The label of the row or chip that opened it. */
  title: string;
  booking: LiveCallBooking;
  isCoach: boolean;
  tz: string;
  onPickTime: () => void;
  onClose: () => void;
}) {
  const { showError } = useToast();
  const [sending, setSending] = useState<number | null>(null);
  const other = firstNameOf(isCoach ? booking.playerName : booking.coachName, isCoach ? 'your player' : 'your coach');

  const send = async (minutes: number) => {
    setSending(minutes);
    try {
      await adjustLiveLessonTime({ bookingId: booking.id, minutes });
      onClose();
    } catch (err) {
      showError(liveLessonsErrorMessage(err, 'Could not move the lesson. Please try again.'));
    } finally {
      setSending(null);
    }
  };

  const chip = (minutes: number) => (
    <button
      key={minutes}
      type="button"
      onClick={() => { void send(minutes); }}
      disabled={sending !== null}
      aria-busy={sending === minutes}
      aria-label={`${minutes < 0 ? 'Earlier' : 'Later'} by ${Math.abs(minutes)} minutes`}
      style={busyStyle(sending === minutes)}
      className={
        'ios-press h-11 flex-1 cursor-pointer rounded-[100px] bg-white text-[15px] leading-[20px] font-medium tabular-nums text-db-text-1 ' +
        'border border-[rgba(0,0,0,0.08)] disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40'
      }
    >
      {sending === minutes ? <ButtonSpinner size={16} /> : Math.abs(minutes)}
    </button>
  );

  return (
    <Sheet title={title} onClose={onClose} busy={sending !== null}>
      <div className={`flex min-h-[52px] items-center gap-3 px-4 ${CARD}`}>
        <span className={`${ROW_TEXT} flex-1`}>Now</span>
        <span className={`${ROW_VALUE} tabular-nums`}>{whenLine(booking.scheduledStartAt, tz)}</span>
      </div>
      <h3 className={`mt-6 ${SECTION_HEADER}`}>Earlier, minutes</h3>
      <div className="flex gap-2">{STEPS.map((m) => chip(-m))}</div>
      <h3 className={`mt-6 ${SECTION_HEADER}`}>Later, minutes</h3>
      <div className="flex gap-2">{STEPS.map((m) => chip(m))}</div>
      <p className={SECTION_FOOTNOTE}>{other} confirms with one tap.</p>
      <button type="button" onClick={onPickTime} disabled={sending !== null} className={`${BTN_SECONDARY_ON_PAGE} mb-2 mt-6 w-full`}>
        Reschedule
      </button>
    </Sheet>
  );
}
